/**
 * Casca do app: sidebar, barra de abas, cabeçalho, busca, notificações e
 * atalhos de teclado. É o que fica na tela o tempo todo, em qualquer módulo.
 *
 * `modules` é indexado pelo ID do módulo, o mesmo usado no roteamento e no
 * MODULE_FEATURE_MAP. O ID nunca muda com o idioma.
 */
const shell = {
  brand: 'MeuGantt',

  modules: {
    gantt: 'Gantt',
    kanban: 'Kanban',
    team: 'Equipe',
    schedule: 'Cronograma',
    calendar: 'Calendário',
    todos: 'Pendências',
    raci: 'RACI',
    eap: 'EAP',
    risks: 'Riscos',
    changes: 'Mudanças',
    costs: 'Custos',
    home: 'Início',
    portfolio: 'Portfólio',
    mytasks: 'Minhas Tarefas',
    wiki: 'Ajuda',
    settings: 'Configurações',
  },

  nav: {
    lockedHint: '{{module}}, disponível no plano {{plan}}',
    ariaTabs: 'Modulos do projeto',
    searchHint: 'Pesquisar (Ctrl+K)',
    loading: 'Carregando...',
    noProjects: 'Nenhum projeto',
    projectNamePlaceholder: 'Nome do projeto',
    help: 'Ajuda',
    helpHint: 'Ajuda e Suporte',
    settings: 'Configurações',
    close: 'Fechar',
    deleteProject: 'Excluir projeto',
    removeFromOrg: 'Remover da organização',
    ariaSidebar: 'Menu lateral de projetos',
    closeMenu: 'Fechar menu',
    search: 'Pesquisar',
    notifications: 'Notificações',
    themeDark: 'Tema Escuro',
    themeLight: 'Tema Claro',
    logout: 'Sair',
    collapse: 'Recolher',
    expand: 'Expandir',
    myProjects: 'MEUS PROJETOS',
    favorites: 'FAVORITOS',
    shared: 'Compartilhados',
    /** Menu do botão de ajuda, no rodapé da sidebar. */
    knowledgeBase: 'Base de Conhecimento',
    contactSupport: 'Falar com Suporte',
    /** Tooltip do botão de tema: descreve o destino, não o estado atual. */
    themeToLight: 'Mudar para tema claro',
    themeToDark: 'Mudar para tema escuro',
    /** Fallback do rotulo da conta quando nao ha nome nem e-mail carregado. */
    account: 'Minha conta',
  },

  project: {
    new: 'Novo Projeto',
    favorite: 'Favoritar',
    unfavorite: 'Desfavoritar',
    addToFavorites: 'Adicionar aos favoritos',
    removeFromFavorites: 'Remover dos favoritos',
    limitTitle: 'Limite de Projetos',
    limitBody: 'Você atingiu o limite de projetos do seu plano atual.',
    /** Mesma frase com o número do limite, quando ele é conhecido. */
    limitBodyCount: 'Você atingiu o limite de {{limit}} projetos do seu plano atual.',
    unlimited: 'Ilimitado',
    /** Menu de contexto do projeto na sidebar. "Org" é abreviação de propósito: o item é estreito. */
    rename: 'Renomear',
    duplicate: 'Duplicar',
    moveToOrg: 'Mover para Org',
    removeFromOrgShort: 'Remover da Org',
    delete: 'Excluir',
    createError: 'Erro ao criar projeto:',
    deleteError: 'Erro ao excluir projeto:',
    removeFromOrgError: 'Erro ao remover projeto da organização:',
    duplicateError: 'Erro ao duplicar projeto:',
    renameError: 'Erro ao renomear projeto:',
  },

  /** Pastas de projetos: a gaveta que agrupa a carteira de um cliente. */
  folder: {
    new: 'Nova pasta',
    namePlaceholder: 'Nome da pasta (ex: Lumiere)',
    actions: 'Ações da pasta',
    rename: 'Renomear pasta',
    delete: 'Excluir pasta',
    empty: 'Pasta vazia',
    /** Rótulo do grupo que junta quem ficou fora de qualquer pasta. */
    unfiled: 'Sem pasta',
    moveTo: 'Mover para pasta',
    removeFrom: 'Tirar da pasta',
    /** Mesma ação do lado da pasta: quem cria a gaveta procura a ação nela. */
    addProject: 'Adicionar projeto',
    created: 'Pasta "{{name}}" criada',
    moved: 'Guardado em "{{name}}"',
    movedOut: 'Tirado da pasta',
    /** Ação do toast: mover é reversível, e dizer isso é o que faz o usuário testar. */
    undo: 'Desfazer',
    sessionError: 'Sessão expirada. Recarregue a página e tente de novo.',
    /** A frase precisa dizer que o projeto sobrevive, senão ninguém arrisca excluir. */
    confirmDelete: 'Excluir a pasta "{{name}}"? Os projetos dentro dela não são excluídos, apenas voltam para "Sem pasta".',
    createError: 'Erro ao criar pasta:',
    renameError: 'Erro ao renomear pasta:',
    deleteError: 'Erro ao excluir pasta:',
    moveError: 'Erro ao mover projeto para a pasta:',
  },

  /**
   * Diálogo de opções de exportação.
   *
   * O contador de páginas é o coração desta tela: ele muda ao vivo quando o usuário
   * desmarca uma coluna ou troca o papel. Nenhuma das ferramentas pesquisadas mostra isso
   * antes de gerar, e a queixa campeã do ClickUp (205 votos) é exatamente "exportei, não
   * era isso, exportei de novo".
   */
  exportDialog: {
    title: 'Exportar cronograma',
    formatLabel: 'Formato',
    formatPdf: 'PDF',
    formatImage: 'Imagem',
    formatExcel: 'Excel',
    columnsLabel: 'Colunas no documento',
    columnsHint: 'Não muda as colunas da sua tela.',
    presetsLabel: 'Modelos',
    presetExecutive: 'Diretoria',
    presetTeam: 'Equipe',
    presetClient: 'Cliente',
    presetAll: 'Tudo',
    pageLabel: 'Página',
    paper: 'Papel',
    /* Designação ISO 216: igual nos três idiomas hoje, mas mora no dicionário porque
       mercado americano usa Letter/Legal, e aí a diferença aparece aqui e não no JSX. */
    paperA4: 'A4',
    paperA3: 'A3',
    paperA2: 'A2',
    orientation: 'Orientação',
    portrait: 'Retrato',
    landscape: 'Paisagem',
    scale: 'Escala',
    scaleActual: 'Tamanho real',
    scaleFit: 'Caber na largura',
    scaleZoom: 'Reduzir a 75%',
    pagesForms: { one: 'página', other: 'páginas' },
    /** `{{wide}}` colunas por `{{tall}}` linhas de folhas. */
    pagesGrid: '{{wide}} de largura por {{tall}} de altura',
    anchorWarning: 'A tabela é larga demais para se repetir em cada página: as folhas da direita sairão só com as barras. Tire colunas ou use um papel maior.',
    excelHint: 'A planilha leva todas as tarefas e todas as colunas, com abas de baseline, equipe, sprints e curva S.',
    imageHint: 'A imagem sai numa peça só, no tema que você está vendo.',
    cancel: 'Cancelar',
    confirm: 'Exportar',
    exporting: 'Exportando...',
  },
  header: {
    share: 'Compartilhar',
    shareHint: 'Compartilhar projeto com membros',
    close: 'Fechar',
    /**
     * Indexado pelo `viewMode` (`days|weeks|months`), que é gravado em
     * `projects.settings` e comparado no código. O valor fica lá; só o rótulo
     * mora aqui.
     */
    viewModes: {
      days: 'Dias',
      weeks: 'Semanas',
      months: 'Meses',
    },
    team: 'Equipe',
    settings: 'Configurações',
    export: 'Exportar',
    exporting: 'Exportando...',
    exportPdf: 'Exportar PDF',
    exportImage: 'Exportar Imagem',
    columns: 'Colunas',
    exportExcel: 'Exportar Excel',
    /** Toast de falha da exportação. O detalhe técnico fica no console. */
    exportError: 'Erro ao exportar. Tente novamente.',
    layoutTableOnly: 'Só tabela',
    layoutBoth: 'Tabela + Gantt',
    layoutGanttOnly: 'Só Gantt',
    publish: 'Publicar',
    viewMenu: 'Exibir',
    viewMenuLayoutGroup: 'Visão',
    viewMenuAnalysisGroup: 'Análise',
    viewMenuLayout: 'Layout',
    viewMenuZoom: 'Zoom',
    viewMenuOpen: 'Abrir',
    viewMenuOn: 'Ligado',
    viewMenuOff: 'Desligado',
    shortcutsTitle: 'Atalhos de Teclado',
    shortcutsHint: 'Atalhos de teclado',
  },

  search: {
    placeholder: 'Buscar em todos os projetos...',
    minChars: 'Digite pelo menos 2 caracteres para buscar',
    noResults: 'Nenhum resultado encontrado para "{{query}}"',
    hint: 'Atalho: Ctrl+K',
    searching: 'Buscando...',
    resultTasks: 'Tarefas',
    resultRisk: 'Risco',
    resultChange: 'Mudança',
    resultCost: 'Custo',
    resultPendencia: 'Pendência',
    navigate: 'navegar',
    select: 'selecionar',
    close: 'fechar',
  },

  /**
   * Grupos e rótulos dos atalhos. As COMBINAÇÕES de tecla (Ctrl+K, Shift+Tab)
   * não são traduzidas: sao o que esta impresso no teclado.
   */
  shortcuts: {
    groups: {
      navigation: 'Navegação',
      editing: 'Edição',
      selection: 'Seleção',
    },
    list: {
      globalSearch: 'Busca global',
      goToGantt: 'Ir para Gantt',
      goToKanban: 'Ir para Kanban',
      goToCalendar: 'Ir para Calendario',
      goToTeam: 'Ir para Equipe',
      goToHome: 'Ir para Home',
      undo: 'Desfazer',
      redo: 'Refazer',
      indentTask: 'Indentar tarefa',
      outdentTask: 'Desindentar tarefa',
      moveTaskUp: 'Mover tarefa acima',
      moveTaskDown: 'Mover tarefa abaixo',
      duplicateTask: 'Duplicar tarefa',
      pasteTasks: 'Colar tarefas',
      deleteTask: 'Excluir tarefa',
      multiSelect: 'Selecao multipla',
      rangeSelect: 'Selecao em intervalo',
      clearSelect: 'Limpar selecao',
    },
    globalSearch: 'Busca global',
    goTo: 'Ir para {{module}}',
    undo: 'Desfazer',
    redo: 'Refazer',
    indent: 'Aumentar recuo (indent)',
    outdent: 'Reduzir recuo (outdent)',
    moveUp: 'Mover para cima',
    moveDown: 'Mover para baixo',
    duplicate: 'Duplicar tarefa',
    paste: 'Colar tarefas',
    deleteSelected: 'Excluir selecionada(s)',
    selectMultiple: '+Clique Selecionar múltiplas',
    selectRange: '+Clique Selecionar intervalo',
    clearSelection: 'Limpar seleção',
    /**
     * `{{key}}` é o SLOT da tecla (renderizada como <kbd>), não texto: a
     * posição dela muda com o idioma, então a frase não pode ser quebrada em
     * "antes" e "depois" fixos.
     */
    hint: 'Pressione {{key}} em qualquer tela para abrir este painel',
  },

  /**
   * Mensagens de notificação. `{{target}}` marca onde entra o nome da tarefa ou
   * do projeto, que é renderizado em negrito. É um SLOT e não uma concatenação
   * justamente porque a posição muda com o idioma: em português o prazo vem
   * antes do "está próximo", em inglês vem depois do verbo.
   *
   * As frases que começam com espaço são precedidas pelo nome de quem agiu.
   * `deadlineSoon` e `deadlineOverdue` não têm esse espaço nem maiúscula
   * pendente: são geradas pelo sistema, sem ator, e ficam sozinhas na linha.
   */
  notifications: {
    title: 'Notificações',
    /**
     * O vazio ganhou título e descrição quando Notificações virou destino da
     * barra do celular: numa tela inteira, "Nenhuma notificação" sozinho parece
     * falha de carregamento. O título afirma que está tudo certo e a descrição
     * diz o que vai chegar ali, que é o que ensina a pessoa a voltar.
     */
    emptyTitle: 'Tudo em dia',
    emptyDescription: 'Menções, prazos e mudanças nas suas tarefas aparecem aqui.',
    markAllRead: 'Marcar todas como lidas',
    /** Rótulo curto do botão do dropdown, onde "como lidas" não cabe. */
    markAll: 'Marcar todas',
    unread: '({{count}} novas)',
    someone: 'Alguém',
    fallbackTask: 'uma tarefa',
    fallbackProject: 'um projeto',
    mention: ' mencionou você em {{target}}',
    comment: ' comentou em {{target}}',
    taskAssigned: ' atribuiu você à tarefa {{target}}',
    taskUnassigned: ' removeu você da tarefa {{target}}',
    taskStatus: ' atualizou o status de {{target}}',
    deadlineSoon: 'O prazo de {{target}} está próximo',
    deadlineOverdue: 'A tarefa {{target}} está atrasada',
    projectInvite: ' convidou você para o projeto {{target}}',
    capacityOverload: ' sinalizou sobrecarga de {{target}}',
    supportReply: ' respondeu ao seu ticket de suporte',
    supportStatusChange: ' atualizou o status do seu ticket de suporte',
    generic: ' enviou uma notificação',
  },
};

/** en e es derivam desta forma: chave faltando vira erro de compilação. */
export type ShellCopy = typeof shell;

export default shell;
